import React from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SettingsIcon, Volume2Icon, LayoutIcon, LightbulbIcon, MonitorIcon, VideoIcon, CalendarIcon, CheckIcon } from 'lucide-react';
export function ServicesPage() {
  useScrollReveal();
  const services = [{
    id: 'sound',
    icon: Volume2Icon,
    title: 'Sound / Public Address Systems',
    desc: 'Professional sound reinforcement and public address solutions for conferences, ceremonies, concerts, launches, and public events. We ensure crystal-clear audio delivery regardless of venue size or acoustic challenges.',
    idealFor: ['Corporate Conferences', 'Live Concerts', 'Outdoor Ceremonies', 'Public Addresses']
  }, {
    id: 'stage',
    icon: LayoutIcon,
    title: 'Stage Hire',
    desc: 'Reliable staging solutions for indoor and outdoor events, from compact platforms to larger event stage structures. Our stages are built to strict safety standards and can be customized to your specific requirements.',
    idealFor: ['Award Ceremonies', 'Fashion Shows', 'Outdoor Festivals', 'Corporate Presentations']
  }, {
    id: 'lighting',
    icon: LightbulbIcon,
    title: 'Lighting Hire',
    desc: 'Dynamic lighting solutions that enhance visibility, atmosphere, and audience experience for all kinds of events. From subtle ambient lighting to complex programmed light shows.',
    idealFor: ['Gala Dinners', 'Theatrical Productions', 'Product Launches', 'Live Entertainment']
  }, {
    id: 'visual',
    icon: MonitorIcon,
    title: 'Visual Equipment Hire',
    desc: 'Screens, projection, and visual presentation equipment that help your message reach your audience clearly and professionally. We provide high-resolution LED walls and seamless projection systems.',
    idealFor: ['Corporate Workshops', 'Exhibitions', 'Large Scale Presentations', 'Hybrid Events']
  }, {
    id: 'video',
    icon: VideoIcon,
    title: 'DVD & Video Production',
    desc: 'Video production support for events, presentations, recordings, and branded visual content. Capture your event professionally for archival, broadcast, or promotional purposes.',
    idealFor: ['Event Documentation', 'Live Streaming', 'Corporate Promos', 'Training Videos']
  }, {
    id: 'management',
    icon: CalendarIcon,
    title: 'Events & Promotions Management',
    desc: 'End-to-end support for event coordination, activation environments, branded experiences, and production delivery. We take the stress out of technical event planning.',
    idealFor: ['Brand Activations', 'Corporate Retreats', 'Public Festivals', 'Roadshows']
  }];
  return <main className="w-full" data-id="element-459">
      <PageHero title="Our Services" subtitle="Comprehensive technical solutions designed to make your events and installations a resounding success." icon={SettingsIcon} data-id="element-460" />

      <div className="bg-brand-black py-10" data-id="element-461">
        {services.map((service, index) => <section key={service.id} className={`py-20 px-6 ${index % 2 !== 0 ? 'bg-brand-charcoal' : 'bg-brand-black'}`} data-id="element-462">
            <div className="max-w-7xl mx-auto" data-id="element-463">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`} data-id="element-464">
                <div className={`reveal ${index % 2 !== 0 ? 'lg:order-2' : 'lg:order-1'}`} data-id="element-465">
                  <div className="w-16 h-16 rounded-2xl bg-brand-blue/10 flex items-center justify-center mb-8 text-brand-blue border border-brand-blue/20" data-id="element-466">
                    <service.icon className="w-8 h-8" data-id="element-467" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-468">
                    {service.title}
                  </h2>
                  <p className="text-brand-muted-light text-lg leading-relaxed mb-8" data-id="element-469">
                    {service.desc}
                  </p>

                  <div className="mb-10" data-id="element-470">
                    <h4 className="text-white font-semibold mb-4 uppercase tracking-wider text-sm text-brand-cyan" data-id="element-471">
                      Ideal For:
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-472">
                      {service.idealFor.map((item, i) => <li key={i} className="flex items-center gap-3 text-brand-muted" data-id="element-473">
                          <CheckIcon className="w-5 h-5 text-brand-blue shrink-0" data-id="element-474" />
                          {item}
                        </li>)}
                    </ul>
                  </div>

                  <Link to={`/contact?service=${service.id}`} className="btn-outline" data-id="element-475">
                    Inquire About This Service
                  </Link>
                </div>

                <div className={`reveal ${index % 2 !== 0 ? 'lg:order-1' : 'lg:order-2'}`} style={{
              transitionDelay: '200ms'
            }} data-id="element-476">
                  <div className="img-placeholder aspect-[4/3] w-full shadow-glass" data-id="element-477">
                    <service.icon className="w-20 h-20 text-white/10" data-id="element-478" />
                  </div>
                </div>
              </div>
            </div>
          </section>)}
      </div>

      <section className="py-24 px-6 bg-brand-charcoal text-center reveal border-t border-white/5" data-id="element-479">
        <div className="max-w-3xl mx-auto" data-id="element-480">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-481">
            Need a Custom Solution?
          </h2>
          <p className="text-brand-muted-light mb-8 text-lg" data-id="element-482">
            We can tailor our services to meet your specific technical
            requirements.
          </p>
          <Link to="/contact" className="btn-glow" data-id="element-483">
            Discuss Your Project
          </Link>
        </div>
      </section>
    </main>;
}